import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { AppLayout } from '../components/AppLayout'
import { DiamondCard } from '../components/DiamondCard'
import { DiamondTable } from '../components/DiamondTable'
import { FilterSidebar } from '../components/FilterSidebar'
import { MediaModal } from '../components/MediaModal'
import { ShareDialog } from '../components/ShareDialog'
import { searchApi, trackApi } from '../api/endpoints'
import { seedRepresentatives } from '../lib/representativeImages'
import { stoneKey } from '../lib/stoneKey'
import { downloadStonesCsv } from '../lib/stoneExport'
import { filtersFromParams, paramsFromFilters, intensitiesFromColorFacet, parseSupplierFacets, type SupplierFacet } from '../lib/filterHelpers'
import { useApp } from '../store'
import { PeerBandsProvider } from '../components/StonePeer'
import { StoneCompare } from '../components/StoneCompare'
import { feedback } from '../lib/feedback'
import { useModalFocus } from '../lib/modalFocus'
import { ApiError, type Diamond, type PeerBand, type SearchFilters } from '../api/types'

/**
 * /search — filter sidebar on the left, result grid on the right.
 * URL params hydrate the filter state so a search is shareable.
 *
 * Read flow: filters change → debounced fetch to /api/search → results
 * land in store, render as a grid of DiamondCard. Sort dropdown lives
 * in the toolbar above the grid.
 */
// Default sort = $/carat ascending. The diamond trade compares stones on
// price-per-carat, not total — Vikram called out (2026-07-26 demo) that buyers
// misread the big total as if it were per-carat. Leading with $/ct low-to-high
// makes the primary axis unambiguous. Total-price + carat sorts stay available.
const SORT_OPTIONS: { label: string; value: NonNullable<SearchFilters['sortBy']> }[] = [
  { label: '$/ct: low to high', value: 'ppc_asc' },
  { label: '$/ct: high to low', value: 'ppc_desc' },
  { label: 'Total price: low to high', value: 'price_asc' },
  { label: 'Total price: high to low', value: 'price_desc' },
  { label: 'Largest first', value: 'carat_desc' },
  { label: 'Smallest first', value: 'carat_asc' },
]
const DEFAULT_SORT: NonNullable<SearchFilters['sortBy']> = 'ppc_asc'

// Stones per page. Numbered pager (Tej: pages, not load-more); the
// server caps limit at 500 so 50 is comfortably within bounds.
// 48, not 50: the card grid is 2 / 3 / 4 columns, and 48 is divisible by all
// three so the LAST row is always full (12×4, 16×3, 24×2) — 50 left a 2-stone
// stub row at 4-up. Pagination math below derives everything from this.
const PAGE_SIZE = 48

export function Search() {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const { searchResults, setSearchResults, signedIn } = useApp()
  const reduce = useReducedMotion()
  // An anonymous viewer pressing "save stone" on a row gets taken somewhere,
  // never a silent 401. `useCallback` because DiamondTable/Row are memoized —
  // a fresh identity every render would undo that and reconcile all 50 rows.
  const requireSignIn = useCallback(() => {
    navigate(`/signin?next=${encodeURIComponent(window.location.pathname + window.location.search)}`)
  }, [navigate])

  const [filters, setFilters] = useState<SearchFilters>(() => filtersFromParams(params))
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [error, setError] = useState<string | null>(null)
  // Bumped by the error card's Retry — re-runs the fetch effect without
  // touching filters/page, so a transient failure isn't a dead end.
  const [retryTick, setRetryTick] = useState(0)
  const [demoNotice, setDemoNotice] = useState(false)
  // $/ct distributions for the peer buckets on this page. Undefined for an
  // anonymous or unverified caller — the server withholds them with the
  // prices, and the mark then simply does not render.
  const [peerBands, setPeerBands] = useState<Record<string, PeerBand> | undefined>(undefined)
  const [total, setTotal] = useState(0)
  const [sort, setSort] = useState<NonNullable<SearchFilters['sortBy']>>(
    (params.get('sort') as SearchFilters['sortBy']) || DEFAULT_SORT
  )
  const [openStone, setOpenStone] = useState<Diamond | null>(null)
  // Mobile filter drawer — filters stack above results on <768px otherwise,
  // forcing a phone user to scroll the whole panel to reach the stones.
  const [filtersOpen, setFiltersOpen] = useState(false)
  // Modal prev/next across the current result page (arrow keys + chevrons).
  const openIdx = openStone
    ? searchResults.findIndex((x) => (x.uniqueId ?? x.stockId) === (openStone.uniqueId ?? openStone.stockId))
    : -1
  const stepStone = (delta: number) => {
    const next = searchResults[openIdx + delta]
    if (next) setOpenStone(next)
  }
  const [viewMode, setViewMode] = useState<'cards' | 'table'>(() => {
    // Table is the default search view (Tej 2026-07-27) — brokers scan the grid,
    // not cards. The storage key is versioned (2026-08-04 aesthetic pass) so
    // every browser resets to table once; a choice made after that still wins.
    if (typeof window === 'undefined') return 'table'
    return (window.localStorage.getItem('aska:searchView2') as 'cards' | 'table') || 'table'
  })
  // Share selection (#56) — the broker sharing flow lives on the catalog table:
  // tick rows, then "Create share". Keyed by stoneKey; keeps the Diamond so we
  // have the stockId for the share payload. Signed-in only (share create is authed).
  const [selected, setSelected] = useState<Map<string, Diamond>>(new Map())
  const [shareOpen, setShareOpen] = useState(false)
  // Compare reads the SAME pin set as Share. It is a lens on the selection,
  // not a second one — two selection models on one list is how you get a
  // buyer ticking rows that turn out to be the wrong ticks.
  const [compareOpen, setCompareOpen] = useState(false)
  const compareRef = useRef<HTMLDivElement>(null)
  const closeCompare = useCallback(() => { setCompareOpen(false) }, [])
  // Focus in, trapped, Escape from anywhere, and back to the Compare button on
  // close. All four were missing; see lib/modalFocus.ts.
  useModalFocus(compareOpen, compareRef, { onEscape: closeCompare })
  // Stable identities (useCallback, no deps — both use the functional setter)
  // so the memoized DiamondTable/Row don't re-render all 50 rows every time the
  // modal opens/closes (openStone lives on this component). This is the main
  // fix for the open/close interaction lag: a modal flip must NOT reconcile the
  // whole table + 50 StoneImage subtrees.
  const toggleSelect = useCallback((s: Diamond) => {
    const key = stoneKey(s)
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(key)) next.delete(key)
      else next.set(key, s)
      return next
    })
  }, [])
  const toggleSelectAll = useCallback((pageStones: Diamond[]) => {
    setSelected((prev) => {
      const next = new Map(prev)
      const allOn = pageStones.length > 0 && pageStones.every((s) => next.has(stoneKey(s)))
      if (allOn) pageStones.forEach((s) => next.delete(stoneKey(s)))
      else pageStones.forEach((s) => next.set(stoneKey(s), s))
      return next
    })
  }, [])
  const selectedStockIds = useMemo(
    () => [...selected.values()].map((s) => s.stockId).filter(Boolean),
    [selected],
  )
  // Memoize the Set passed to DiamondTable so its identity only changes when the
  // selection actually changes — a fresh `new Set(...)` every render would bust
  // the table's React.memo on every modal open/close.
  const selectedKeys = useMemo(
    () => (signedIn ? new Set(selected.keys()) : undefined),
    [signedIn, selected],
  )
  const [availableColors, setAvailableColors] = useState<Set<string> | null>(null)
  const [availableFancy, setAvailableFancy] = useState<Set<string> | null>(null)
  const [availableIntensities, setAvailableIntensities] = useState<Set<string> | null>(null)
  const [supplierFacets, setSupplierFacets] = useState<SupplierFacet[] | null>(null)
  const [availableShapes, setAvailableShapes] = useState<Set<string> | null>(null)
  const [availableClarities, setAvailableClarities] = useState<Set<string> | null>(null)
  const [availablePolish, setAvailablePolish] = useState<Set<string> | null>(null)
  const [availableSymmetry, setAvailableSymmetry] = useState<Set<string> | null>(null)
  const [availableFluorescence, setAvailableFluorescence] = useState<Set<string> | null>(null)
  const [availableLab, setAvailableLab] = useState<Set<string> | null>(null)
  const [availableGrowth, setAvailableGrowth] = useState<Set<string> | null>(null)
  const [availableCountries, setAvailableCountries] = useState<Set<string> | null>(null)
  // Desktop-only: collapse the filter rail so the results table gets full width
  // (Vikram, 2026-07-26 — "filter tab should hide, table resizes bigger").
  // DEFAULT COLLAPSED since 2026-09-20 (Tej: "default hide filters" — the
  // catalog leads, filters are a reach-for); the choice persists like the
  // table/cards view preference does.
  const [railCollapsed, setRailCollapsed] = useState(() => {
    try { return localStorage.getItem('aska:filterRail') !== 'open' } catch { return true }
  })
  useEffect(() => {
    try { localStorage.setItem('aska:filterRail', railCollapsed ? 'closed' : 'open') } catch { /* private mode */ }
  }, [railCollapsed])

  // Chip universe from the server's full-inventory facets (every /search
  // response carries them). Guarantees Round + every real color appears
  // even though the page itself only holds PAGE_SIZE stones.
  function consumeFacets(facets?: Record<string, Record<string, number>>) {
    if (!facets) return
    const whites = new Set<string>()
    const fancies = new Set<string>()
    for (const c of Object.keys(facets.color || {})) {
      const t = c.trim()
      if (!t) continue
      if (/^[A-Z]$/i.test(t)) whites.add(t.toUpperCase())
      else {
        const parts = t.split(/\s+/).filter(Boolean)
        const hue = parts[parts.length - 1]
        if (hue && /^[A-Za-z]+$/.test(hue)) {
          fancies.add(hue.charAt(0).toUpperCase() + hue.slice(1).toLowerCase())
        }
      }
    }
    setAvailableColors(whites)
    setAvailableFancy(fancies)
    setAvailableIntensities(intensitiesFromColorFacet(facets.color))
    // KYC-gated supplier facet ("id:name" keys) — null (section hidden) for
    // anon / un-KYC'd callers because the server omits the dimension entirely.
    setSupplierFacets(parseSupplierFacets(facets.supplier))
    setAvailableShapes(new Set(Object.keys(facets.shape || {}).filter(Boolean)))
    setAvailableClarities(new Set(Object.keys(facets.clarity || {}).map((c) => c.toUpperCase())))
    // Tier-1 grade facets (#41) — exact canonical strings from normalizeStone,
    // so chip labels match facet keys 1:1 (e.g. "Very Good", "None", "CVD").
    const keySet = (dim: string) => new Set(Object.keys(facets[dim] || {}).filter(Boolean))
    setAvailablePolish(keySet('polish'))
    setAvailableSymmetry(keySet('symmetry'))
    setAvailableFluorescence(keySet('fluorescence'))
    setAvailableLab(keySet('lab'))
    setAvailableGrowth(keySet('stoneType'))
    setAvailableCountries(keySet('country'))
  }

  // Sync params back to URL whenever filters change. Lets users share
  // a result page or hit back to a previous search.
  useEffect(() => {
    setParams(paramsFromFilters({ ...filters, sortBy: sort }), { replace: true })
  }, [filters, sort, setParams])

  // Any filter/sort change goes back to page 1 (a stale deep page under
  // a new filter set is meaningless).
  const filterKey = JSON.stringify({ ...filters, sortBy: sort })
  useEffect(() => { setPage(1) }, [filterKey])

  // Fetch on filter/sort/page change. Debounce is 200ms — feels live
  // without hammering Firestore on every keystroke in the carat input.
  // The cleanup cancels the in-flight debounce, so the transient
  // fetch that fires between a filter change and its page-1 reset
  // never actually runs.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const handle = setTimeout(async () => {
      try {
        const r = await searchApi.query({
          ...filters, sortBy: sort, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE,
        })
        if (cancelled) return
        // Warm the representative-image cache before the cards mount so
        // the first 404 already has a real same-shape photo to fall to.
        seedRepresentatives(r.results)
        setSearchResults(r.results)
        setTotal(r.total)
        setDemoNotice(!!r.isDemoFallback)
        setPeerBands(r.peerBands)
        consumeFacets(r.facets)
        setError(null)
        // Recommender signal (feeds "matchesPastSearches") — signed-in,
        // page-1, real intent only; fire-and-forget.
        if (signedIn && page === 1) {
          const intent = Object.values(filters).some((v) =>
            Array.isArray(v) ? v.length > 0 : v != null && v !== false)
          if (intent) trackApi.search({ ...filters, sortBy: sort })
        }
      } catch (err) {
        if (cancelled) return
        // Real server errors (4xx/5xx) now surface here instead of
        // silently falling back to demo data.
        setError(err instanceof ApiError ? err.message : 'Search failed')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 200)
    return () => { cancelled = true; clearTimeout(handle) }
  }, [filterKey, page, retryTick, setSearchResults])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  function goToPage(p: number) {
    const next = Math.min(Math.max(1, p), totalPages)
    if (next === page) return
    setPage(next)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
  // Elided page list: 1 … p-1 p p+1 … N
  const pageItems = useMemo<(number | '…')[]>(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
    const set = new Set<number>([1, totalPages, page - 1, page, page + 1])
    const nums = [...set].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b)
    const out: (number | '…')[] = []
    for (let i = 0; i < nums.length; i++) {
      if (i > 0 && nums[i] - nums[i - 1] > 1) out.push('…')
      out.push(nums[i])
    }
    return out
  }, [page, totalPages])

  // Active-filter count for the mobile "Filters" pill badge. Mirrors the
  // sidebar's own tally so the badge matches what's inside the drawer.
  const activeFilterCount = useMemo(() => (
    (filters.shape?.length ?? 0)
    + (filters.color?.length ?? 0)
    + (filters.clarity?.length ?? 0)
    + (filters.cut?.length ?? 0)
    + (filters.polish?.length ?? 0)
    + (filters.symmetry?.length ?? 0)
    + (filters.fluorescence?.length ?? 0)
    + (filters.lab?.length ?? 0)
    + (filters.stoneType?.length ?? 0)
    + (filters.country?.length ?? 0)
    + (filters.colorLike ? 1 : 0)
    + (filters.fancyHues?.length ?? 0)
    + (filters.fancyIntensities?.length ?? 0)
    + (typeof filters.caratMin === 'number' ? 1 : 0)
    + (typeof filters.caratMax === 'number' ? 1 : 0)
    + (typeof filters.priceMin === 'number' ? 1 : 0)
    + (typeof filters.priceMax === 'number' ? 1 : 0)
    + (typeof filters.ppcMin === 'number' || typeof filters.ppcMax === 'number' ? 1 : 0)
    + (typeof filters.ratioMin === 'number' || typeof filters.ratioMax === 'number' ? 1 : 0)
    + (typeof filters.lengthMin === 'number' || typeof filters.lengthMax === 'number' ? 1 : 0)
    + (typeof filters.widthMin === 'number' || typeof filters.widthMax === 'number' ? 1 : 0)
    + (filters.stockIds?.length ? 1 : 0)
    + (typeof filters.supplierId === 'number' ? 1 : 0)
    + (filters.supplierIds?.length ?? 0)
    + (filters.heartsArrows ? 1 : 0)
    + (filters.eyeClean ? 1 : 0)
    + (filters.noBGM ? 1 : 0)
  ), [filters])

  // Body-scroll-lock + Escape-to-close while the mobile drawer is open.
  useEffect(() => {
    if (!filtersOpen) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFiltersOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prevOverflow
      window.removeEventListener('keydown', onKey)
    }
  }, [filtersOpen])

  // Shared props so the desktop rail and mobile drawer render the SAME
  // interactive FilterSidebar (identical filter state, testids, callbacks).
  const sidebarProps = {
    value: filters,
    onChange: setFilters,
    onReset: () => { setFilters({}); setSort(DEFAULT_SORT) },
    resultCount: total,
    availableColors,
    availableFancy,
    availableIntensities,
    availableShapes,
    availableClarities,
    availablePolish,
    availableSymmetry,
    availableFluorescence,
    availableLab,
    availableGrowth,
    availableCountries,
    suppliers: supplierFacets,
  }

  return (
    <AppLayout>
      {/* Wide, left-leaning shell: the dense catalog table wants width, so use
          most of the viewport (not a narrow centered column that stranded the
          table off to the right). When the rail collapses, the single column
          takes the freed 288px so the table grows (Vikram, 2026-07-26). */}
      {/* Width is view-aware (Tej 2026-10-22): the TABLE is a dense 20+ column
          ledger that wants room — wide shell, minimal gutter. CARDS want
          breathing room from the page edges — a tighter max-width + generous
          gutter — and the grid below drops to smaller tiles (5-up) so the cards
          don't balloon in the extra space. */}
      <div className={`w-full mx-auto py-8 grid grid-cols-1 gap-6 lg:gap-8 ${
        viewMode === 'table'
          ? 'max-w-[1920px] px-4 sm:px-6 lg:px-8'
          : 'max-w-[1760px] px-4 sm:px-6 lg:px-12 xl:px-20'
      } ${railCollapsed ? '' : 'md:grid-cols-[280px_minmax(0,1fr)]'}`}>
        {/* Desktop/tablet left rail — hidden on mobile (filters live in a drawer)
            and hidden on desktop when collapsed so the table gets full width. */}
        {!railCollapsed && (
          <div className="hidden md:block">
            <FilterSidebar {...sidebarProps} />
          </div>
        )}

        <section>
          {/* v2 section header — an editorial page title with the thick
              rule-accent beneath it. The rule anchors the whole results
              column; everything below (toolbar, table, cards) reads as its
              quiet body. Result total lives here as the one key number. */}
          <header className="mb-5">
            <h1 className="font-serif text-[26px] leading-none text-ink tracking-h1">Diamonds</h1>
            <div className="mt-2.5 flex items-center gap-3">
              <span className="rule-accent" aria-hidden />
              {total > 0 && (
                <p className="flex items-baseline gap-1.5 font-mono uppercase tracking-[0.12em] text-navy-300 tabular">
                  <span className="text-[16px] font-bold tracking-normal text-sapphire">{total.toLocaleString()}</span>
                  <span className="text-[12px] font-medium">stones</span>
                </p>
              )}
            </div>
          </header>
          {/* ── Command bar (signature, 2026-08-04) ─────────────────────
              ONE sticky frosted-glass bar carries every results control —
              filters, view, range, sort — floating over the catalog as it
              scrolls (the landing's liquid-glass chrome brought into
              browse). Controls inside are quiet white pills; the glass
              does the material work. Glass stays chrome-only: data never
              sits on it. */}
          <div className="sticky top-[57px] z-30 mb-5 -mx-1">
            <div className="rounded-xl border border-line bg-cream/85 px-2.5 py-2 backdrop-blur-xl flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
                {/* Mobile-only Filters trigger — opens the drawer (desktop uses the rail). */}
                <button
                  type="button"
                  onClick={() => setFiltersOpen(true)}
                  aria-label="Open filters"
                  aria-haspopup="dialog"
                  className="md:hidden inline-flex items-center gap-2 h-9 rounded-full border border-line bg-paper/70 pl-3.5 pr-4 text-[13px] font-semibold text-ink hover:border-navy-300 transition-colors"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="18" x2="20" y2="18" />
                    <circle cx="9" cy="6" r="2" fill="white" /><circle cx="15" cy="12" r="2" fill="white" /><circle cx="9" cy="18" r="2" fill="white" />
                  </svg>
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="inline-flex items-center justify-center rounded-full bg-navy text-white text-[10px] font-bold h-4 min-w-[16px] px-1">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
                {/* Desktop-only rail toggle — collapse filters so the table widens
                    (Vikram, 2026-07-26). Mobile uses the drawer button above. */}
                <button
                  type="button"
                  onClick={() => setRailCollapsed((v) => !v)}
                  aria-pressed={railCollapsed}
                  className="hidden md:inline-flex items-center gap-2 h-9 rounded-full border border-line bg-paper/70 pl-3.5 pr-4 text-[13px] font-semibold text-ink hover:border-navy-300 transition-colors"
                  data-testid="rail-toggle"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="18" x2="20" y2="18" />
                    <circle cx="9" cy="6" r="2" fill="white" /><circle cx="15" cy="12" r="2" fill="white" /><circle cx="9" cy="18" r="2" fill="white" />
                  </svg>
                  {railCollapsed ? 'Show filters' : 'Hide filters'}
                  {railCollapsed && activeFilterCount > 0 && (
                    <span className="inline-flex items-center justify-center rounded-full bg-navy text-white text-[10px] font-bold h-4 min-w-[16px] px-1">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
                <div
                  role="tablist"
                  aria-label="View mode"
                  className="inline-flex rounded-full border border-line bg-paper/70 p-0.5"
                >
                  <ViewToggleButton
                    active={viewMode === 'table'}
                    onClick={() => { setViewMode('table'); window.localStorage.setItem('aska:searchView2', 'table') }}
                    label="Table"
                    testId="view-table"
                    icon={
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
                      </svg>
                    }
                  />
                  <ViewToggleButton
                    active={viewMode === 'cards'}
                    onClick={() => { setViewMode('cards'); window.localStorage.setItem('aska:searchView2', 'cards') }}
                    label="Cards"
                    testId="view-cards"
                    icon={
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
                        <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
                      </svg>
                    }
                  />
                </div>
                {!error && searchResults.length > 0 && (
                  <p className="hidden lg:block pl-1 text-[12.5px] text-muted tabular" data-testid="result-count">
                    Showing <span className="font-mono font-semibold text-ink">{((page - 1) * PAGE_SIZE + 1).toLocaleString()}–{Math.min(page * PAGE_SIZE, total).toLocaleString()}</span> of <span className="font-mono font-semibold text-ink">{total.toLocaleString()}</span>
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 sm:pr-0.5">
                <span className="hidden sm:inline text-[10.5px] font-semibold uppercase tracking-[0.12em] text-soft">Sort</span>
                {/* Native arrow removed (appearance-none) + label kept OUTSIDE the
                    control so the selected value never overlaps a prefix label
                    (Safari rendered the old overlaid "Sort by:" on top of it). */}
                <div className="relative">
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value as NonNullable<SearchFilters['sortBy']>)}
                    className="h-9 appearance-none rounded-full border border-line bg-paper/70 pl-4 pr-9 text-[13px] font-medium text-ink focus:border-gold focus:outline-none transition-colors cursor-pointer"
                    aria-label="Sort"
                    data-testid="sort-select"
                  >
                    {SORT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <svg className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-soft" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                </div>
              </div>
            </div>
          </div>

          <ActiveFilterPills filters={filters} onChange={setFilters} />

          {/* Share-selection bar (#56) — appears once rows are ticked. Broker
              flow: pick stones → Create share (link + Excel at your markup). */}
          {signedIn && selected.size > 0 && (
            <div
              className="sticky top-[116px] z-20 mb-4 flex items-center justify-between gap-3 rounded-full border border-gold/40 bg-paper/90 px-4 py-2.5 shadow-elev-2 backdrop-blur"
              data-testid="share-select-bar"
            >
              <span className="text-sm font-semibold text-ink tabular">
                {selected.size} selected
              </span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setSelected(new Map())}
                  className="text-xs font-semibold text-soft hover:text-ink transition-colors"
                >
                  Clear
                </button>
                {/* COMPARE — the verb this screen never had. Two pins is the
                    minimum for a comparison; below that the button would open
                    a panel that can only say "pin another stone". */}
                <button
                  type="button"
                  onClick={() => { feedback.select(); setCompareOpen(true) }}
                  disabled={selected.size < 2}
                  title={selected.size < 2 ? 'Tick a second stone to compare' : undefined}
                  className="inline-flex h-9 items-center gap-1.5 rounded-full border border-navy-900/15 px-4 text-xs font-semibold text-ink transition-all hover:border-navy-900/35 active:scale-[0.97] disabled:opacity-40 disabled:pointer-events-none"
                  data-testid="compare-selected"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <rect x="3" y="4" width="7" height="16" rx="1.5" /><rect x="14" y="4" width="7" height="16" rx="1.5" />
                  </svg>
                  Compare ({selected.size})
                </button>
                {/* Download the SELECTED stones as a spreadsheet — the raw
                    catalog rows at the buyer's own verified prices, NO markup
                    (that's the share flow). Pure client export of already-
                    loaded data; emits a CSV (Excel-openable) because there is
                    no client-side .xlsx builder in the bundle. */}
                <button
                  type="button"
                  onClick={() => downloadStonesCsv([...selected.values()])}
                  className="inline-flex h-9 items-center gap-1.5 rounded-full border border-navy-900/15 px-4 text-xs font-semibold text-ink transition-all hover:border-navy-900/35 active:scale-[0.97]"
                  data-testid="download-selected"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download .csv
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="inline-flex h-9 items-center gap-1.5 rounded-full bg-navy px-4 text-xs font-semibold text-white hover:bg-navy-hover active:scale-[0.97] transition-all"
                  data-testid="share-selected"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.6" y1="13.5" x2="15.4" y2="17.5" /><line x1="15.4" y1="6.5" x2="8.6" y2="10.5" />
                  </svg>
                  Create share
                </button>
              </div>
            </div>
          )}

          {demoNotice && (
            <div
              className="mb-4 rounded-card border border-warn/40 bg-warn/10 px-4 py-3 text-sm text-warn"
              data-testid="demo-fallback-banner"
              role="status"
            >
              Showing cached demo data — live inventory unreachable.
            </div>
          )}
          {error ? (
            <div className="panel rounded-lg border-danger/30 p-6 text-center">
              <p className="text-sm text-danger">{error}</p>
              {/* Retry, so a blip isn't a dead end. */}
              {/* TWO ways out, not one. Retry alone is a loop when the cause is
                  a pathological filter set — which is the most common cause —
                  so the second exit clears the filters that produced it. */}
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => { setError(null); setRetryTick((t) => t + 1) }}
                  className="btn-secondary h-9 px-5 text-xs"
                  data-testid="search-retry"
                >
                  Try again
                </button>
                {activeFilterCount > 0 && (
                  <button
                    type="button"
                    onClick={() => { setError(null); setFilters({}); setSort(DEFAULT_SORT) }}
                    className="btn-secondary h-9 px-5 text-xs"
                    data-testid="search-error-clear-filters"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            </div>
          ) : loading && searchResults.length === 0 ? (
            viewMode === 'table' ? <SkeletonTable /> : <SkeletonGrid />
          ) : searchResults.length === 0 ? (
            <div className="panel rounded-lg p-10 text-center text-sm text-muted">
              {/* NO FILTERS + NO RESULTS used to say "try widening one of the
                  filters" with no filters to widen and no button — a genuine
                  dead end whose only exit was the browser back button. It is
                  not the buyer's search that failed there, it is us. */}
              {activeFilterCount > 0 ? (
                <>
                  <p>No matches. Try widening one of the filters.</p>
                  <button
                    type="button"
                    onClick={() => { setFilters({}); setSort(DEFAULT_SORT) }}
                    className="btn-secondary mt-4 h-9 px-5 text-xs"
                    data-testid="search-clear-filters"
                  >
                    Clear all filters
                  </button>
                </>
              ) : (
                <>
                  <p>The catalog isn't returning stones right now.</p>
                  <button
                    type="button"
                    onClick={() => setRetryTick((t) => t + 1)}
                    className="btn-secondary mt-4 h-9 px-5 text-xs"
                    data-testid="search-empty-retry"
                  >
                    Try again
                  </button>
                </>
              )}
            </div>
          ) : (
            /* While a refetch is in flight over EXISTING results, dim the stale
               data slightly so a sort/filter click visibly registered. */
            <PeerBandsProvider bands={peerBands}>
            <div className={`transition-opacity duration-default ${loading ? 'opacity-60' : ''}`} aria-busy={loading || undefined}>
            {/* Crossfade the whole view on a Cards⇄Table toggle — the swap used
                to be an instant, unanimated DOM replace that read as a freeze.
                mode="wait" fades the old view out then the new one in, keyed by
                viewMode; the stagger below is now a quick 0.012 cascade. */}
            <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={viewMode}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduce ? 0 : 0.14, ease: 'easeOut' }}
            >
            {viewMode === 'table' ? (
            <DiamondTable
              stones={searchResults}
              onOpen={setOpenStone}
              blurPrice={!signedIn}
              sort={sort}
              onSort={setSort}
              selectedKeys={selectedKeys}
              onToggleSelect={signedIn ? toggleSelect : undefined}
              onToggleAll={signedIn ? toggleSelectAll : undefined}
              onRequireSignIn={requireSignIn}
            />
          ) : (
            <motion.div
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5"
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: reduce ? 0 : 0.012 } } }}
            >
              {searchResults.map((s, i) => (
                <motion.div
                  key={stoneKey(s)}
                  variants={reduce
                    ? { hidden: { opacity: 0 }, visible: { opacity: 1 } }
                    : { hidden: { opacity: 0, y: 6 }, visible: { opacity: 1, y: 0 } }}
                  transition={{ duration: reduce ? 0.12 : 0.18, ease: [0.16, 1, 0.3, 1] }}
                  className="w-full min-w-0"
                >
                  <DiamondCard
                    stone={s}
                    onOpen={setOpenStone}
                    blurPrice={!signedIn}
                    index={i}
                    // Same selection Map the table's row checkboxes drive —
                    // the Compare/Create-share bar lights up in CARD view too
                    // (it was table-only before; Tej 2026-09-20).
                    selected={selectedKeys?.has(stoneKey(s)) ?? false}
                    onToggleSelect={signedIn ? () => toggleSelect(s) : undefined}
                  />
                </motion.div>
              ))}
            </motion.div>
          )}
            </motion.div>
            </AnimatePresence>
            </div>
            </PeerBandsProvider>
          )}

          {!error && totalPages > 1 && searchResults.length > 0 && (
            <nav className="mt-8 flex items-center justify-center gap-1.5" data-testid="pager" aria-label="Pagination">
              <button type="button" onClick={() => goToPage(page - 1)} disabled={page <= 1}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-paper text-ink hover:border-navy-300 disabled:opacity-40 disabled:hover:bg-paper disabled:hover:border-line transition-colors">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
              </button>
              {pageItems.map((it, i) => it === '…' ? (
                <span key={`e${i}`} className="px-1.5 text-sm text-soft tabular">…</span>
              ) : (
                <button key={it} type="button" onClick={() => goToPage(it)}
                  aria-current={it === page ? 'page' : undefined}
                  className={it === page
                    ? 'h-9 min-w-9 px-3 rounded-full bg-ink text-paper text-sm font-semibold tabular'
                    : 'h-9 min-w-9 px-3 rounded-full border border-line bg-paper text-sm text-ink tabular hover:border-navy-300 transition-colors'}>
                  {it}
                </button>
              ))}
              <button type="button" onClick={() => goToPage(page + 1)} disabled={page >= totalPages}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-paper text-ink hover:border-navy-300 disabled:opacity-40 disabled:hover:bg-paper disabled:hover:border-line transition-colors">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
              </button>
            </nav>
          )}
        </section>
      </div>

      <MediaModal
        open={!!openStone}
        onClose={() => setOpenStone(null)}
        stone={openStone}
        onPrev={openIdx > 0 ? () => stepStone(-1) : undefined}
        onNext={openIdx >= 0 && openIdx < searchResults.length - 1 ? () => stepStone(1) : undefined}
        blurPrice={!signedIn}
      />

      {/* COMPARE PANEL. A right-anchored drawer over a full-viewport scrim —
          and therefore MODAL, which the code now says out loud.
          It used to be commented as "deliberately NOT a modal: the results stay
          mounted and interactive behind it, so a buyer can keep ticking rows".
          That was never true of the shipped build: the scrim covers the whole
          viewport and swallows every click on the list, so the interaction the
          comment promised was already impossible, while nothing focused the
          drawer, trapped Tab or restored focus — a keyboard user opened it and
          then tabbed the catalogue they could no longer click.
          Both halves are now honest: `aria-modal`, focus managed
          (`useModalFocus`), and pins are managed from INSIDE the panel, where
          Unpin already lives. Exits: unpin a column, Escape (from anywhere),
          the close button, a column header (→ that stone's detail), the scrim. */}
      <AnimatePresence>
        {compareOpen && selected.size > 0 && (
          <>
            {/* A light scrim, not a blackout: the results stay legible behind,
                which is the point of a drawer. It is also the exit most people
                reach for first, so it must exist. */}
            <motion.div
              className="fixed inset-0 z-40 bg-navy-900/15"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              onClick={closeCompare}
              aria-hidden
            />
            <motion.div
              ref={compareRef}
              tabIndex={-1}
              className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[min(680px,100vw)] flex-col focus:outline-none"
              initial={reduce ? { opacity: 0 } : { opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, x: 24 }}
              transition={{ duration: reduce ? 0.14 : 0.28, ease: [0.16, 1, 0.3, 1] }}
              role="dialog"
              aria-modal="true"
              aria-label="Compare stones"
              data-testid="compare-drawer"
            >
              <div className="panel flex min-h-0 flex-1 flex-col overflow-hidden rounded-none border-l shadow-elev-4 md:rounded-l-lg">
                {/* The bands have to be in scope HERE too — the provider around
                    the results does not reach a fixed-position drawer, and
                    without this the "Vs. comparable" row silently vanished for
                    a verified buyer. */}
                <PeerBandsProvider bands={peerBands}>
                  <StoneCompare
                    stones={[...selected.values()]}
                    mult={1}
                    onUnpin={toggleSelect}
                    onOpen={(s) => setOpenStone(s)}
                    onClose={closeCompare}
                    className="min-h-0 flex-1"
                  />
                </PeerBandsProvider>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      {/* Clearing every pin empties the panel; close it rather than leave an
          empty drawer on screen with nothing in it. */}
      {compareOpen && selected.size === 0 && <ClosePanelOnEmpty onEmpty={() => setCompareOpen(false)} />}

      {/* Share-with-markup dialog (#56/#57) — driven by the table selection. */}
      <ShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        stockIds={selectedStockIds}
      />

      {/* Mobile filter drawer — reuses the SAME FilterSidebar (bare) so all
          filters, testids and callbacks stay identical to the desktop rail. */}
      <AnimatePresence>
        {filtersOpen && (
          <motion.div
            className="fixed inset-0 z-50 md:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Filters"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setFiltersOpen(false)}
              aria-hidden
            />
            {/* Left sheet */}
            <motion.div
              className="absolute inset-y-0 left-0 flex h-full w-[88%] max-w-[360px] flex-col bg-paper shadow-2xl"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex items-center justify-between border-b border-border bg-paper px-5 py-4">
                <p className="font-serif text-xl font-semibold text-ink tracking-tight">Filters</p>
                <button
                  type="button"
                  onClick={() => setFiltersOpen(false)}
                  aria-label="Close filters"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-paper text-ink hover:border-navy-300 transition-colors"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-5 py-5">
                <FilterSidebar {...sidebarProps} bare />
              </div>

              <div className="border-t border-border bg-paper px-5 py-4">
                <button
                  type="button"
                  onClick={() => setFiltersOpen(false)}
                  className="w-full h-12 rounded-full bg-navy text-white text-sm font-semibold shadow-card hover:bg-navy/90 active:scale-[0.99] transition-all"
                >
                  {total === 0 ? 'Show results' : `Show ${total.toLocaleString()} result${total === 1 ? '' : 's'}`}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AppLayout>
  )
}

function ViewToggleButton({
  active, onClick, label, icon, testId,
}: {
  active: boolean
  onClick: () => void
  label: string
  icon: React.ReactNode
  testId: string
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full px-4 h-9 text-xs font-semibold transition-colors ${
        active ? 'bg-ink text-paper' : 'text-navy-500 hover:text-ink'
      }`}
    >
      {icon}<span>{label}</span>
    </button>
  )
}

/**
 * Removable active-filter pills above the grid — makes a big-inventory
 * filter set feel controllable (Blue Nile / Rare Carat pattern). Each pill
 * clears exactly its facet; the sidebar's Clear still nukes everything.
 * Multi-value facets (shape, color, clarity, cut) get one pill each; the
 * range facets collapse to a single pill that clears both bounds.
 */
function ActiveFilterPills({
  filters, onChange,
}: {
  filters: SearchFilters
  onChange: (next: SearchFilters) => void
}) {
  const pills: { key: string; label: string; onClear: () => void }[] = []
  const dropFrom = (k: 'shape' | 'color' | 'clarity' | 'cut' | 'polish' | 'symmetry' | 'fluorescence' | 'lab' | 'stoneType' | 'country', item: string) => {
    const next = (filters[k] || []).filter((v) => v !== item)
    onChange({ ...filters, [k]: next.length ? next : undefined })
  }
  for (const s of filters.shape || []) pills.push({ key: `shape-${s}`, label: s, onClear: () => dropFrom('shape', s) })
  for (const c of filters.color || []) pills.push({ key: `color-${c}`, label: `Color ${c}`, onClear: () => dropFrom('color', c) })
  if (filters.colorLike) pills.push({ key: 'fancy', label: `Fancy ${filters.colorLike}`, onClear: () => onChange({ ...filters, colorLike: undefined }) })
  for (const h of filters.fancyHues || []) pills.push({
    key: `fancyhue-${h}`, label: `Fancy ${h}`,
    onClear: () => { const next = (filters.fancyHues || []).filter((v) => v !== h); onChange({ ...filters, fancyHues: next.length ? next : undefined }) },
  })
  for (const g of filters.fancyIntensities || []) pills.push({
    key: `intensity-${g}`, label: g,
    onClear: () => { const next = (filters.fancyIntensities || []).filter((v) => v !== g); onChange({ ...filters, fancyIntensities: next.length ? next : undefined }) },
  })
  if (filters.noBGM) pills.push({ key: 'nobgm', label: 'No BGM', onClear: () => onChange({ ...filters, noBGM: undefined }) })
  for (const c of filters.clarity || []) pills.push({ key: `clarity-${c}`, label: c, onClear: () => dropFrom('clarity', c) })
  if (filters.eyeClean) pills.push({ key: 'eyeclean', label: 'Eye Clean', onClear: () => onChange({ ...filters, eyeClean: undefined }) })
  for (const c of filters.cut || []) pills.push({ key: `cut-${c}`, label: `Cut ${c}`, onClear: () => dropFrom('cut', c) })
  if (filters.heartsArrows) pills.push({ key: 'ha', label: 'Hearts & Arrows', onClear: () => onChange({ ...filters, heartsArrows: undefined }) })
  for (const c of filters.polish || []) pills.push({ key: `pol-${c}`, label: `Pol ${c}`, onClear: () => dropFrom('polish', c) })
  for (const c of filters.symmetry || []) pills.push({ key: `sym-${c}`, label: `Sym ${c}`, onClear: () => dropFrom('symmetry', c) })
  for (const c of filters.fluorescence || []) pills.push({ key: `fluo-${c}`, label: `Fluo ${c}`, onClear: () => dropFrom('fluorescence', c) })
  for (const c of filters.lab || []) pills.push({ key: `lab-${c}`, label: c, onClear: () => dropFrom('lab', c) })
  for (const c of filters.stoneType || []) pills.push({ key: `growth-${c}`, label: c, onClear: () => dropFrom('stoneType', c) })
  // Location pills say "Not X" when the list is an exclusion; dropping the
  // last one also clears the (now-meaningless) exclude flag.
  for (const c of filters.country || []) pills.push({
    key: `country-${c}`, label: filters.countryExclude ? `Not ${c}` : c,
    onClear: () => {
      const next = (filters.country || []).filter((v) => v !== c)
      onChange({ ...filters, country: next.length ? next : undefined, countryExclude: next.length ? filters.countryExclude : undefined })
    },
  })
  if (typeof filters.caratMin === 'number' || typeof filters.caratMax === 'number') {
    pills.push({
      key: 'carat',
      label: `${filters.caratMin ?? '0'}–${filters.caratMax ?? '∞'} ct`,
      onClear: () => onChange({ ...filters, caratMin: undefined, caratMax: undefined }),
    })
  }
  if (typeof filters.priceMin === 'number' || typeof filters.priceMax === 'number') {
    pills.push({
      key: 'price',
      label: `$${filters.priceMin?.toLocaleString() ?? '0'}–$${filters.priceMax?.toLocaleString() ?? '∞'}`,
      onClear: () => onChange({ ...filters, priceMin: undefined, priceMax: undefined }),
    })
  }
  if (typeof filters.ppcMin === 'number' || typeof filters.ppcMax === 'number') {
    pills.push({
      key: 'ppc',
      label: `$${filters.ppcMin?.toLocaleString() ?? '0'}–$${filters.ppcMax?.toLocaleString() ?? '∞'} /ct`,
      onClear: () => onChange({ ...filters, ppcMin: undefined, ppcMax: undefined }),
    })
  }
  if (typeof filters.ratioMin === 'number' || typeof filters.ratioMax === 'number') {
    pills.push({
      key: 'ratio',
      label: `Ratio ${filters.ratioMin ?? '0'}–${filters.ratioMax ?? '∞'}`,
      onClear: () => onChange({ ...filters, ratioMin: undefined, ratioMax: undefined }),
    })
  }
  if (typeof filters.lengthMin === 'number' || typeof filters.lengthMax === 'number') {
    pills.push({
      key: 'length',
      label: `L ${filters.lengthMin ?? '0'}–${filters.lengthMax ?? '∞'}mm`,
      onClear: () => onChange({ ...filters, lengthMin: undefined, lengthMax: undefined }),
    })
  }
  if (typeof filters.widthMin === 'number' || typeof filters.widthMax === 'number') {
    pills.push({
      key: 'width',
      label: `W ${filters.widthMin ?? '0'}–${filters.widthMax ?? '∞'}mm`,
      onClear: () => onChange({ ...filters, widthMin: undefined, widthMax: undefined }),
    })
  }
  if (typeof filters.supplierId === 'number') {
    pills.push({ key: 'supplier', label: `Supplier #${filters.supplierId}`, onClear: () => onChange({ ...filters, supplierId: undefined }) })
  }
  for (const id of filters.supplierIds || []) pills.push({
    key: `suppliers-${id}`, label: filters.supplierExclude ? `Not supplier #${id}` : `Supplier #${id}`,
    onClear: () => {
      const next = (filters.supplierIds || []).filter((v) => v !== id)
      onChange({ ...filters, supplierIds: next.length ? next : undefined, supplierExclude: next.length ? filters.supplierExclude : undefined })
    },
  })

  if (pills.length === 0) return null
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="active-filters">
      {pills.map((p) => (
        <button
          key={p.key}
          type="button"
          onClick={p.onClear}
          className="group inline-flex items-center gap-1.5 h-8 rounded-full border border-gold/45 bg-paper pl-3 pr-2 text-xs font-semibold text-ink shadow-[0_1px_2px_rgba(10,31,60,0.06)] transition-all duration-150 hover:border-gold hover:bg-gold-050 active:scale-[0.96]"
          aria-label={`Remove filter ${p.label}`}
        >
          {p.label}
          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full text-gold group-hover:text-ink">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </span>
        </button>
      ))}
    </div>
  )
}

/** Fires once when the compare panel's pin set empties out. A drawer with
 *  nothing in it is a dead end whose only exit is a close button the user has
 *  no reason to look for. */
function ClosePanelOnEmpty({ onEmpty }: { onEmpty: () => void }) {
  useEffect(() => { onEmpty() }, [onEmpty])
  return null
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
      {Array.from({ length: 12 }).map((_, i) => (
        <div
          key={i}
          className="panel rounded-lg overflow-hidden"
        >
          <div className="aspect-square w-full skeleton" />
          <div className="p-3.5 space-y-2">
            <div className="h-4 w-2/3 skeleton rounded" />
            <div className="h-3 w-3/4 skeleton rounded" />
          </div>
        </div>
      ))}
    </div>
  )
}

/** Table-shaped loading state — the default view must never flash a card
 *  grid while the first page loads. Mirrors the real table's rhythm:
 *  header band over the thick ink rule, then hairline rows. */
function SkeletonTable() {
  return (
    <div className="panel rounded-lg overflow-hidden" aria-hidden>
      <div className="flex items-center gap-6 border-b-2 border-ink/80 px-4 py-3.5">
        {[48, 40, 72, 96, 64, 40, 40, 40, 56, 72].map((w, i) => (
          <div key={i} className="h-2.5 skeleton rounded" style={{ width: w }} />
        ))}
      </div>
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className="flex items-center gap-6 border-t border-line px-4 py-2.5 first:border-t-0">
          <div className="h-9 w-9 shrink-0 skeleton rounded-full" />
          <div className="h-3 w-24 skeleton rounded" />
          <div className="h-3 w-40 skeleton rounded hidden md:block" />
          <div className="h-3 w-16 skeleton rounded" />
          <div className="h-3 w-28 skeleton rounded hidden lg:block" />
          <div className="ml-auto h-3 w-20 skeleton rounded" />
        </div>
      ))}
    </div>
  )
}

// URL params <-> filters serialization lives in lib/filterHelpers (shared
// with the mobile shell + unit-tested for round-trip fidelity).
